<template>
  <div class="admin-invoices-table">
    <!-- Bulk actions bar + date range -->
    <div class="d-flex align-items-center mb-3 gap-2 flex-wrap">
      <span class="fw-bold">{{ selectedInvoices.length }} selected</span>
      <button
        class="btn btn-sm btn-subtle-primary px-3 py-1"
        :disabled="payableInvoiceIds.length === 0"
        :class="{ disabled: payableInvoiceIds.length === 0 }"
        @click="markInvoicesAsPaid"
      >
        <font-awesome-icon icon="check" class="me-1 fs-9" />
        Mark as paid ({{ payableInvoiceIds.length }})
      </button>
      <button
        class="btn btn-sm btn-subtle-danger px-3 py-1"
        :disabled="cancelableInvoiceIds.length === 0"
        :class="{ disabled: cancelableInvoiceIds.length === 0 }"
        :title="cancelButtonTitle"
        @click="markInvoicesAsCanceled"
      >
        <font-awesome-icon icon="times" class="me-1 fs-9" />
        Cancel invoices ({{ cancelableInvoiceIds.length }})
      </button>
      <div class="ms-auto d-flex align-items-center gap-2">
        <span class="text-muted small">Date range:</span>
        <DatePicker
          v-model="dateRange"
          selectionMode="range"
          :manualInput="false"
          dateFormat="dd/mm/yy"
          placeholder="Select range"
          showButtonBar
          appendTo="body"
          class="date-range-picker"
          @date-select="onDateRangeSelect"
          @clear-click="onDateRangeClear"
        />
      </div>
    </div>

    <!-- Loading State -->
    <LoadingIndicatorComponent v-if="loading" />

    <DataTable
      v-show="!loading"
      v-model:selection="selectedInvoices"
      v-model:filters="filters"
      :value="rows"
      dataKey="id"
      filterDisplay="row"
      sortField="requested_at"
      :sortOrder="-1"
      class="border rounded"
      :scrollable="shouldUseVirtualScroller"
      :scrollHeight="computedScrollHeight"
      :virtualScrollerOptions="computedVirtualScrollerOptions"
      :rowClass="rowClass"
    >
      <template #empty>
        <div class="text-center py-4 text-muted">No invoices found.</div>
      </template>

      <!-- Checkbox selection -->
      <Column selectionMode="multiple" headerStyle="width: 3rem" />

      <!-- ID -->
      <Column field="id" header="ID" sortable :headerStyle="{ width: '5rem' }">
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span class="badge text-bg-warning">{{ data.id }}</span>
        </template>
      </Column>

      <!-- Company -->
      <Column
        field="company_name"
        header="Company"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '14rem' }"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <a
            href="#"
            class="text-decoration-none"
            title="Open this company's billing detail"
            @click.prevent="$emit('view:company', data.company_id)"
          >
            {{ data.company_name }} (id: {{ data.company_id }})
          </a>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            placeholder="Search..."
          />
        </template>
      </Column>

      <!-- Invoice # -->
      <Column
        field="invoice_number"
        header="Invoice #"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span class="fw-bold">{{ data.invoice_number }}</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            placeholder="Search..."
          />
        </template>
      </Column>

      <!-- Status -->
      <Column
        field="status"
        header="Status"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '8rem' }"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span class="badge" :class="getStatusBadgeClass(data.status)">
            {{ data.status }}
          </span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Filter"
            class="p-column-filter w-100"
            display="chip"
            appendTo="body"
            :focusOnHover="false"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                Active
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
            <template #option="slotProps">
              <span
                class="badge"
                :class="getStatusBadgeClass(slotProps.option.value)"
              >
                {{ slotProps.option.label }}
              </span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Date -->
      <Column
        field="requested_at"
        header="Date"
        sortable
        :headerStyle="{ width: '10rem' }"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          {{ formatInvoiceDate(data) }}
        </template>
      </Column>

      <!-- Amount -->
      <Column
        field="display_amount"
        sortField="amount_sort_key"
        header="Amount"
        sortable
        :headerStyle="{ width: '7rem' }"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <div class="text-end">
            {{ formatMoneyWithCurrency(data.display_amount, data.currency) }}
          </div>
        </template>
      </Column>

      <!-- Actions -->
      <Column header="Actions" :headerStyle="{ width: '10rem' }">
        <template #body="{ data }">
          <div class="d-flex gap-1">
            <button
              v-if="data.invoice_type === 'fee'"
              class="btn btn-outline-warning btn-sm"
              @click="regenerateInvoice(data)"
              :disabled="loadingInvoiceIds.has(data.id)"
              title="Regenerate"
            >
              <font-awesome-icon
                v-if="loadingInvoiceIds.has(data.id)"
                icon="spinner"
                spin
                class="px-1"
              />
              <font-awesome-icon v-else icon="recycle" class="px-1" />
            </button>
            <button
              v-if="canSendToMerit(data)"
              class="btn btn-outline-warning btn-sm"
              @click="sendInvoiceToMerit(data)"
              :disabled="loadingInvoiceIds.has(data.id)"
              title="Send to Merit"
            >
              <font-awesome-icon
                v-if="loadingInvoiceIds.has(data.id)"
                icon="spinner"
                spin
                class="px-1"
              />
              <font-awesome-icon v-else icon="paper-plane" class="px-1" />
            </button>
            <button
              class="btn btn-outline-primary btn-sm"
              @click="downloadInvoice(data)"
              :disabled="loadingInvoiceIds.has(data.id)"
              title="Download"
            >
              <font-awesome-icon
                v-if="loadingInvoiceIds.has(data.id)"
                icon="spinner"
                spin
                class="px-1"
              />
              <font-awesome-icon v-else icon="download" class="px-1" />
            </button>
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import {
  formatDateTimeIntoDate,
  formatMoneyWithCurrency,
} from "@/composables/formatUtils";
import { currencyGroupedAmount } from "@/composables/money/invoiceUtils";
import { useFileDownload } from "@/composables/useFileDownload";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { Invoice } from "@/types";
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import DatePicker from "primevue/datepicker";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getSortIcon } from "@/utils/sortIcons";

export interface InvoiceRow extends Invoice {
  status: string;
  company_name: string;
  amount_sort_key?: number;
}

const props = defineProps<{
  invoices: InvoiceRow[];
  dateFrom: Date;
  dateTo: Date | null;
  loading: boolean;
}>();

const emit = defineEmits<{
  "invoices-changed": [];
  "date-range-changed": [dateFrom: Date, dateTo: Date | null];
  "view:company": [companyId: number];
}>();

defineOptions({
  name: "AdminInvoicesTable",
});

const MAX_HEIGHT = 600;
const ITEM_HEIGHT = 42;

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const fileDownload = useFileDownload();
const selectedInvoicesRaw = ref<InvoiceRow[]>([]);
const selectedInvoices = computed({
  get: () => selectedInvoicesRaw.value,
  set: (val) => {
    selectedInvoicesRaw.value = val.filter((inv) => isRowSelectable(inv));
  },
});
const loadingInvoiceIds = ref<Set<number>>(new Set());

// Date range picker state — synced from props so the picker always reflects the parent's source of truth
const dateRange = ref<Date[] | null>([
  props.dateFrom,
  props.dateTo ?? new Date(),
]);

watch(
  () => [props.dateFrom, props.dateTo] as const,
  ([newFrom, newTo]) => {
    dateRange.value = newTo ? [newFrom, newTo] : [newFrom, new Date()];
  }
);

const onDateRangeSelect = () => {
  if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
    emit("date-range-changed", dateRange.value[0], dateRange.value[1]);
  }
};

const onDateRangeClear = () => {
  const defaultFrom = new Date();
  defaultFrom.setMonth(defaultFrom.getMonth() - 3);
  defaultFrom.setHours(0, 0, 0, 0);
  dateRange.value = null;
  emit("date-range-changed", defaultFrom, null);
};

// Virtual scrolling — same pattern as PortfolioItemsTable
// Amounts sort within their currency, never across currencies.
const rows = computed(() =>
  props.invoices.map((invoice) => ({
    ...invoice,
    amount_sort_key: currencyGroupedAmount(invoice),
  }))
);

const shouldUseVirtualScroller = computed(() => props.invoices.length > 14);
const computedScrollHeight = computed(() =>
  shouldUseVirtualScroller.value ? `${MAX_HEIGHT}px` : undefined
);
const computedVirtualScrollerOptions = computed(() =>
  shouldUseVirtualScroller.value
    ? { itemSize: ITEM_HEIGHT, numToleratedItems: 20, autoSize: true }
    : undefined
);

const filters = ref({
  company_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  invoice_number: { value: null, matchMode: FilterMatchMode.CONTAINS },
  status: { value: [], matchMode: FilterMatchMode.IN },
});

const statusOptions = [
  { label: "Requested", value: "requested" },
  { label: "Paid", value: "paid" },
  { label: "Canceled", value: "canceled" },
  { label: "Drafting", value: "drafting" },
];

const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case "paid":
      return "bg-success";
    case "canceled":
      return "bg-danger";
    case "requested":
      return "bg-primary";
    case "drafting":
      return "bg-secondary";
    default:
      return "bg-secondary";
  }
};

const formatInvoiceDate = (invoice: InvoiceRow): string => {
  if (invoice.requested_at) {
    return formatDateTimeIntoDate(invoice.requested_at);
  } else if (invoice.created_at) {
    return formatDateTimeIntoDate(invoice.created_at);
  }
  return "-";
};

const isRowSelectable = (row: InvoiceRow): boolean => {
  if (row.status !== "requested") return false;
  if (row.invoice_type === "expense") return true;
  // Any unpaid fee/flat-fee invoice can be marked paid (e.g. settlement fee invoices
  // awaiting the company's payment); cancelability is filtered separately below.
  if (row.invoice_type === "fee" || row.invoice_type === "flat_fee")
    return true;
  return false;
};

const rowClass = (data: InvoiceRow) => {
  return isRowSelectable(data) ? "" : "checkbox-hidden";
};

// Payable: only requested invoices
const payableInvoiceIds = computed(() =>
  selectedInvoices.value
    .filter((inv) => inv.status === "requested")
    .map((inv) => inv.id)
);

// Cancelable: requested expense invoices OR fee-only fee invoices.
// Mixed selection (expense + fee-only) is rejected — backend dispatches per type and a
// single bulk action should be unambiguous about what's being canceled.
const selectedCancelable = computed(() =>
  selectedInvoices.value.filter(
    (inv) =>
      inv.status === "requested" &&
      (inv.invoice_type === "expense" ||
        (inv.invoice_type === "fee" && inv.is_fee_only))
  )
);

const hasMixedCancelableTypes = computed(() => {
  const types = new Set(
    selectedCancelable.value.map((inv) => inv.invoice_type)
  );
  return types.size > 1;
});

const cancelableInvoiceIds = computed(() =>
  hasMixedCancelableTypes.value
    ? []
    : selectedCancelable.value.map((inv) => inv.id)
);

const cancelButtonTitle = computed(() =>
  hasMixedCancelableTypes.value ? t("invoice.cancel_mixed_types_disabled") : ""
);

const canSendToMerit = (invoice: InvoiceRow): boolean => {
  const canBeRegistered = ["fee", "flat_fee", "expense"].includes(
    invoice.invoice_type
  );
  const hasMeritMetadata =
    invoice.merit_metadata && Object.keys(invoice.merit_metadata).length > 0;
  return canBeRegistered && !hasMeritMetadata;
};

const markInvoicesAsPaid = async () => {
  try {
    const response = await goSolidApi.post("/api/admin/pay-invoices", {
      invoice_ids: payableInvoiceIds.value,
    });
    if (response.status === 200) {
      selectedInvoices.value = [];
      emit("invoices-changed");
    } else {
      throw new Error("Error paying invoices.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

const markInvoicesAsCanceled = async () => {
  try {
    const response = await goSolidApi.post("/api/admin/cancel-invoices", {
      invoice_ids: cancelableInvoiceIds.value,
    });
    if (response.status === 200) {
      selectedInvoices.value = [];
      emit("invoices-changed");
    } else {
      throw new Error("Error canceling invoices.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

const regenerateInvoice = async (invoice: InvoiceRow) => {
  if (confirm(t("invoice.regenerate_confirmation"))) {
    loadingInvoiceIds.value.add(invoice.id);
    try {
      const response = await goSolidApi.post(
        `/api/admin/fee_invoice/${invoice.id}/regenerate`
      );
      if (response.status === 200) {
        window.location.reload();
      } else {
        throw new Error(t("invoice.error_downloading"));
      }
    } catch (error: unknown) {
      globalErrorHandler(error);
    } finally {
      loadingInvoiceIds.value.delete(invoice.id);
    }
  }
};

const sendInvoiceToMerit = async (invoice: InvoiceRow) => {
  if (confirm(t("invoice.send_to_merit_confirmation"))) {
    loadingInvoiceIds.value.add(invoice.id);
    try {
      const invoiceTypePrefix =
        invoice.invoice_type === "expense" ? "expense" : "fee";
      const response = await goSolidApi.post(
        `/api/admin/${invoiceTypePrefix}_invoice/${invoice.id}/send_to_merit`
      );
      const result = response.data?.result;
      if (typeof result === "string" && result === "already_registered") {
        globalSuccessHandler("Success", t("invoice.already_in_merit"));
      }
    } catch (error: unknown) {
      globalErrorHandler(error);
    } finally {
      loadingInvoiceIds.value.delete(invoice.id);
    }
  }
};

const downloadInvoice = async (invoice: InvoiceRow) => {
  loadingInvoiceIds.value.add(invoice.id);
  try {
    await fileDownload.downloadInvoice(invoice.id);
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    loadingInvoiceIds.value.delete(invoice.id);
  }
};
</script>

<style scoped lang="scss">
.admin-invoices-table {
  overflow-x: auto;
}

:deep(.p-datatable) {
  .p-datatable-header-cell {
    background-color: #f8f9fa;
  }
}

.badge {
  font-size: 0.75rem;
}

.date-range-picker {
  max-width: 250px;
}

:deep(.checkbox-hidden) .pv-checkbox {
  visibility: hidden;
}
</style>
